import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/pages/Landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setBooking(r.data.booking || null);
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired" || r.data.status === "failed") { setStatus("failed"); return; }
      } catch (e) {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100 antialiased" data-testid="payment-success-page">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-extrabold uppercase text-white">Confirming Your Payment…</h1>
            <p className="text-stone-400 text-sm mt-3">Hold on — we're locking in your date.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 className="w-14 h-14 text-amber-400 mx-auto mb-6" />
            <span className="text-amber-400 font-mono text-xs uppercase tracking-widest font-bold">Booking Confirmed</span>
            <h1 className="text-3xl sm:text-4xl font-extrabold uppercase text-white mt-2">You're On The Boat</h1>
            {booking && (
              <div className="mt-8 bg-stone-950 border border-stone-800 rounded-2xl p-6 text-left" data-testid="booking-summary">
                <div className="flex justify-between text-sm py-2 border-b border-stone-800"><span className="text-stone-400">Trip</span><span className="text-white font-bold">{booking.trip_name}</span></div>
                <div className="flex justify-between text-sm py-2 border-b border-stone-800"><span className="text-stone-400">Date</span><span className="text-white font-bold">{new Date(booking.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span></div>
                <div className="flex justify-between text-sm py-2 border-b border-stone-800"><span className="text-stone-400">Anglers</span><span className="text-white font-bold">{booking.quantity}</span></div>
                <div className="flex justify-between text-sm py-2"><span className="text-stone-400">Paid</span><span className="text-amber-400 font-extrabold">£{Number(booking.amount_gbp).toLocaleString("en-GB")}</span></div>
              </div>
            )}
            <p className="text-stone-400 text-sm mt-6">A confirmation has been sent to your email. Boarding at Noss on Dart Marina, Dartmouth. Any questions, call the skipper on <a href="tel:07929834128" className="text-amber-400 font-bold">07929834128</a>.</p>
            <Link to="/" data-testid="success-home-link" className="inline-block mt-8 px-8 py-3 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold uppercase tracking-wider text-xs rounded transition-colors">Back To Yellowfin</Link>
          </div>
        )}
        {(status === "error" || status === "failed" || status === "timeout") && (
          <div data-testid="payment-unconfirmed">
            <h1 className="text-2xl font-extrabold uppercase text-white">We Couldn't Confirm That Payment</h1>
            <p className="text-stone-400 text-sm mt-3">If money has left your account, don't worry — call <a href="tel:07929834128" className="text-amber-400 font-bold">07929834128</a> and we'll sort your booking straight away.</p>
            <Link to="/book" data-testid="retry-booking-link" className="inline-block mt-8 px-8 py-3 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold uppercase tracking-wider text-xs rounded transition-colors">Try Booking Again</Link>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
